// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Button, Table, Icon, Message, Loader, Input, Dropdown, Label, Pagination } from 'semantic-ui-react';
import { policyTypeApi, PolicyType } from '../api/policyTypeApi';
import { providerApi, Provider } from '../api/providerApi';

function PolicyTypeListPage() {
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const navigate = useNavigate();

  const categoryOptions = [
    { key: '', text: 'All Categories', value: '' },
    { key: 'PENSION', text: 'Pension', value: 'PENSION' },
    { key: 'INSURANCE', text: 'Insurance', value: 'INSURANCE' },
    { key: 'INVESTMENT', text: 'Investment', value: 'INVESTMENT' },
    { key: 'BANKING', text: 'Banking', value: 'BANKING' }
  ];

  const loadProviders = useCallback(async () => {
    try {
      const data = await providerApi.getAll({ isActive: true, size: 1000 });
      setProviders(data.content);
    } catch (err) {
      console.error('Failed to load providers:', err);
    }
  }, []);

  const loadPolicyTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await policyTypeApi.getAll({
        search: searchTerm || undefined,
        providerId: selectedProvider || undefined,
        category: selectedCategory || undefined,
        page: currentPage,
        size: pageSize
      });
      setPolicyTypes(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load policy types');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedProvider, selectedCategory, currentPage, pageSize]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    loadPolicyTypes();
  }, [loadPolicyTypes]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedProvider, selectedCategory]);

  const handleDelete = async (id: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this policy type?')) {
      return;
    }

    try {
      await policyTypeApi.delete(id);
      await loadPolicyTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete policy type');
    }
  };

  const providerOptions = [
    { key: '', text: 'All Providers', value: '' },
    ...providers.map(p => ({ key: p.id!, text: p.name, value: p.id! }))
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'PENSION': return 'blue';
      case 'INSURANCE': return 'green';
      case 'INVESTMENT': return 'orange';
      case 'BANKING': return 'purple';
      default: return 'grey';
    }
  };

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='file alternate' />
        <Header.Content>
          Policy Type Management
          <Header.Subheader>Manage policy types by provider</Header.Subheader>
        </Header.Content>
      </Header>

      {/* Filter Bar */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <Input icon='search' placeholder='Search...' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ flex: 1 }} />
        <Dropdown selection options={providerOptions} value={selectedProvider} onChange={(_e, data) => setSelectedProvider(data.value as string)} placeholder='Filter by Provider' />
        <Dropdown selection options={categoryOptions} value={selectedCategory} onChange={(_e, data) => setSelectedCategory(data.value as string)} placeholder='Filter by Category' />
        <Button primary onClick={() => navigate('/policy-types/new')}><Icon name='plus' />New Policy Type</Button>
      </div>

      {error && <Message negative><Message.Header>Error</Message.Header><p>{error}</p></Message>}

      {loading ? (
        <Loader active inline='centered'>Loading policy types...</Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Provider</Table.HeaderCell>
                <Table.HeaderCell>Email Override</Table.HeaderCell>
                <Table.HeaderCell>Code</Table.HeaderCell>
                <Table.HeaderCell>Category</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {policyTypes.length === 0 ? (
                <Table.Row><Table.Cell colSpan='7' textAlign='center'>No policy types found</Table.Cell></Table.Row>
              ) : (
                policyTypes.map((pt) => (
                  <Table.Row key={pt.id}>
                    <Table.Cell><Header as='h4'>{pt.name}</Header></Table.Cell>
                    <Table.Cell>{pt.providerName || 'Unknown'}</Table.Cell>
                    <Table.Cell>
                      {pt.overrideEmail ? (
                        <div>
                          <Label size='small' color='orange'>
                            <Icon name='mail' />
                            {pt.overrideEmail}
                          </Label>
                          {pt.emailOverrideReason && (
                            <div style={{ fontSize: '0.85em', color: 'grey', marginTop: '4px' }}>
                              <Icon name='info circle' /> {pt.emailOverrideReason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </Table.Cell>
                    <Table.Cell><code>{pt.code}</code></Table.Cell>
                    <Table.Cell><Label color={getCategoryColor(pt.category)}>{pt.category}</Label></Table.Cell>
                    <Table.Cell><Label color={pt.isActive ? 'green' : 'red'}>{pt.isActive ? 'Active' : 'Inactive'}</Label></Table.Cell>
                    <Table.Cell>
                      <Button.Group size='small'>
                        <Button
                          icon='edit'
                          color='blue'
                          onClick={() => navigate(`/policy-types/${pt.id}/edit`)}
                          title='Edit policy type'
                        />
                        <Button
                          icon='trash'
                          color='red'
                          onClick={() => handleDelete(pt.id!)}
                          title='Delete policy type'
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#666' }}>
              Showing {totalElements === 0 ? 0 : currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} policy type{totalElements === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>Items per page:</span>
              <Dropdown
                compact
                selection
                value={pageSize}
                options={[
                  { key: 10, text: '10', value: 10 },
                  { key: 20, text: '20', value: 20 },
                  { key: 50, text: '50', value: 50 },
                  { key: 100, text: '100', value: 100 }
                ]}
                onChange={(_e, data) => {
                  setPageSize(data.value as number);
                  setCurrentPage(0); // Reset to first page when changing page size
                }}
              />
              <Pagination
                activePage={currentPage + 1}
                totalPages={totalPages}
                onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                siblingRange={1}
                boundaryRange={0}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: <Icon name='chevron left' />, icon: true }}
                nextItem={{ content: <Icon name='chevron right' />, icon: true }}
              />
            </div>
          </div>
        </>
      )}

    </Container>
  );
}

export default PolicyTypeListPage;
